import React, { useState } from 'react';
import { Layout, Menu, Space, Typography, theme, Modal } from 'antd';
import { Link, useLocation, useParams, Outlet } from 'react-router-dom';
import useIdleTimer from '@/hooks/useIdleTimer';
import UserProfile from '@/components/common/UserProfile';
import {
  DashboardOutlined,
  SettingOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  AppstoreAddOutlined,
  ShopOutlined,
  AppstoreOutlined
} from '@ant-design/icons';

const { Sider, Header, Content, Footer } = Layout;
const { Title } = Typography;

export default function AdminLayout() {
  const { isSessionTimeoutModalOpen, closeSessionTimeoutModal } = useIdleTimer();
  const { pathname } = useLocation();
  const { propcode } = useParams<{ propcode: string }>();

  // ใช้ Token เพื่อให้สีของ Admin เปลี่ยนตาม Theme ที่ User ตั้งค่าได้ด้วย (Consistency)
  const { token } = theme.useToken();

  const [collapsed, setCollapsed] = useState(false);

  const hiddenMenu = propcode === "newsoft"; // ซ่อนเมนูบางรายการสำหรับ propcode "newsoft"

  const menuItemsRaw = [
    {
      key: `/administrator/${propcode}/admin`,
      icon: <DashboardOutlined />,
      label: <Link to={`/administrator/${propcode}/admin`}>Dashboard</Link>
    },
    {
      key: `/administrator/${propcode}/admin/hotel-list`,
      icon: <AppstoreAddOutlined />,
      label: <Link to={`/administrator/${propcode}/admin/hotel-list`}>Hotel List</Link>,
      hidden: !hiddenMenu // แสดงเฉพาะสำหรับ propcode "newsoft"
    },
    {
      key: 'Hotel',
      icon: <AppstoreAddOutlined />,
      label: 'Hotel',
      children: [
        {
          key: `/administrator/${propcode}/admin/contents/hotel`,
          icon: <ShopOutlined />,
          label: <Link to={`/administrator/${propcode}/admin/contents/hotel`}>Profile</Link>
        },
        {
          key: `/administrator/${propcode}/admin/gallery`,
          icon: <SettingOutlined />,
          label: <Link to={`/administrator/${propcode}/admin/gallery`}>Gallery</Link>
        },
        {
          key: `/administrator/${propcode}/admin/google-map`,
          icon: <SettingOutlined />,
          label: <Link to={`/administrator/${propcode}/admin/google-map`}>Google Map</Link>
        },
      ],
      hidden: hiddenMenu
    },
    {
      key: `/administrator/${propcode}/admin/contents/rooms`,
      icon: <AppstoreOutlined />,
      label: <Link to={`/administrator/${propcode}/admin/contents/rooms`}>Room Details</Link>,
      hidden: hiddenMenu
    },
    {
      key: `/administrator/${propcode}/admin/contents/rates`,
      icon: <AppstoreOutlined />,
      label: <Link to={`/administrator/${propcode}/admin/contents/rates`}>Rate Details</Link>,
      hidden: hiddenMenu
    },
    {
      key: `/administrator/${propcode}/admin/theme`,
      icon: <SettingOutlined />,
      label: <Link to={`/administrator/${propcode}/admin/theme`}>Theme Settings</Link>,
      hidden: import.meta.env.PROD || hiddenMenu
    },
    {
      key: `/administrator/${propcode}/admin/config`,
      icon: <SettingOutlined />,
      label: <Link to={`/administrator/${propcode}/admin/config`}>Config</Link>,
      hidden: hiddenMenu
    },
  ];

  const menuItems = menuItemsRaw.filter(item => !item.hidden);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Modal
        title="Session Timeout"
        open={isSessionTimeoutModalOpen}
        onOk={closeSessionTimeoutModal}
        onCancel={closeSessionTimeoutModal}
        okText="Back to login"
        cancelButtonProps={{ style: { display: 'none' } }}
        mask={{ closable: false }}
      >
        <p>Your session has expired due to inactivity. Please sign in again.</p>
      </Modal>

      {/* Sidebar */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        breakpoint="lg"
        collapsedWidth="0"
        onBreakpoint={(broken) => {
          // ถ้าหน้าจอเล็ก ให้ auto collapse
          if (broken) setCollapsed(true);
        }}
        style={{
          zIndex: 100,
          boxShadow: '2px 0 8px 0 rgba(29,35,41,.05)'
        }}
      >
        {/* Logo Area */}
        <div style={{
          height: 64,
          margin: 16,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: token.borderRadius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: collapsed ? 14 : 18,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            transition: 'all 0.3s'
          }}>
            {collapsed ? 'Admin' : 'Admin Panel'}
          </div>
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[pathname]}
          items={menuItems}
        />
      </Sider>

      {/* Main Layout */}
      <Layout style={{ background: "white" }}> {/* ใช้สีพื้นหลังตาม Theme */}

        {/* Header */}
        <Header style={{
          background: token.colorBgContainer,
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          width: '100%'
        }}>
          {/* Title / Breadcrumb Area */}
          <Space>
            {/* ปุ่ม Toggle Sidebar สำหรับ Desktop (Optional) */}
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: 18, cursor: 'pointer', marginRight: 12, display: 'none' } // ซ่อนไว้ก่อนถ้าใช้ default sider trigger
            })}
            <Title level={4} style={{ margin: 0, fontSize: 20 }}>
              Admin Management
            </Title>
          </Space>

          {/* User Profile Area */}
          <UserProfile />
        </Header>

        {/* Content */}
        <Content style={{ margin: '24px 24px 0', overflow: 'initial' }}>
          <Outlet />
        </Content>

        {/* Footer */}
        <Footer style={{ textAlign: 'center', color: token.colorTextSecondary }}>
          Admin System ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
}
